import {FC, useState} from 'react'
import {useIntl} from 'react-intl'
import SharedData from '../../common/SharedData'
import {IWhyNotWinning} from '../cors/_models'
import {getWhyNotWinning} from '../cors/_requests'
import {currencyLabel} from '../../common/currencyLabel'

/**
 * T9 — on-demand "why didn't I win the buy box" explainer, shown inside the
 * not-winning banner of the offer edit form. Fetches the anonymized
 * breakdown (BE-E) lazily on first expand. The winner is referenced only by
 * its price/delivery metrics — never identity.
 */
const WhyNotWinning: FC<{productSupplierId: number}> = ({productSupplierId}) => {
  const {formatMessage} = useIntl()
  const {currentUser, language} = SharedData()
  const [open, setOpen] = useState(false)
  const [data, setData] = useState<IWhyNotWinning | null>(null)
  const [loading, setLoading] = useState(false)
  const [errored, setErrored] = useState(false)

  const toggle = async () => {
    if (open) {
      setOpen(false)
      return
    }
    if (data) {
      setOpen(true)
      return
    }
    setLoading(true)
    setErrored(false)
    try {
      const res = await getWhyNotWinning(productSupplierId, currentUser?.token, language)
      setData(res.data.data)
      setOpen(true)
    } catch {
      setErrored(true)
    } finally {
      setLoading(false)
    }
  }

  const hours = (h: number) => formatMessage({id: 'offers.why.hours'}, {h})

  return (
    <div className="mt-2">
      <button
        type="button"
        className="btn btn-sm btn-link p-0 text-decoration-none"
        onClick={toggle}
        disabled={loading}
        aria-expanded={open}
      >
        {loading
          ? formatMessage({id: 'offers.why.loading'})
          : open
            ? formatMessage({id: 'offers.why.hide'})
            : formatMessage({id: 'offers.why.cta'})}
        {!loading ? (
          <i className={`fas fa-chevron-${open ? 'up' : 'down'} ms-1 fs-9`} aria-hidden="true" />
        ) : null}
      </button>

      {errored ? (
        <div className="text-danger fs-8 mt-1">{formatMessage({id: 'offers.why.error'})}</div>
      ) : null}

      {open && data ? (
        <div className="mt-2 p-3 rounded bg-light-warning border border-warning border-opacity-25">
          {/* Reason chips */}
          {data.reasons.length > 0 ? (
            <div className="d-flex flex-wrap gap-2 mb-3">
              {data.reasons.map((r) => (
                <span key={r} className="badge badge-light-dark fs-8">
                  {formatMessage({id: `offers.why.reason.${r}`, defaultMessage: r})}
                </span>
              ))}
            </div>
          ) : null}

          {/* Fallback when there's no winner to compare against (the live
              recompute now considers this offer the winner, or there are no
              eligible competing offers) — never leave the panel empty. */}
          {!data.winner ? (
            <div className="fs-8 text-gray-700">
              {data.is_winning
                ? formatMessage({id: 'offers.why.now_winning'})
                : formatMessage({id: 'offers.why.no_competitors'})}
            </div>
          ) : null}

          {/* Anonymized comparison: winner vs mine */}
          {data.winner ? (
            <div className="row g-2 fs-8">
              <div className="col-6 fw-semibold text-muted">&nbsp;</div>
              <div className="col-3 fw-semibold text-muted text-end">{formatMessage({id: 'offers.why.you'})}</div>
              <div className="col-3 fw-semibold text-muted text-end">{formatMessage({id: 'offers.why.winner'})}</div>

              <div className="col-6">{formatMessage({id: 'offers.why.price'})}</div>
              <div className="col-3 text-end">{data.my_offer.effective_price.toFixed(2)}</div>
              <div className="col-3 text-end fw-bold">{data.winner.effective_price.toFixed(2)}</div>

              <div className="col-6">{formatMessage({id: 'offers.why.delivery'})}</div>
              <div className="col-3 text-end">{hours(data.my_offer.delivery_hours)}</div>
              <div className="col-3 text-end fw-bold">{hours(data.winner.delivery_hours)}</div>
            </div>
          ) : null}

          {/* Actionable price suggestion */}
          {data.price_to_win !== null ? (
            <div className="alert alert-success mt-3 mb-0 fs-8 py-2" role="note">
              <i className="fas fa-lightbulb me-1" aria-hidden="true" />
              {formatMessage(
                {id: 'offers.why.price_to_win'},
                {price: data.price_to_win.toFixed(2), currency: currencyLabel(data.currency, formatMessage)},
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default WhyNotWinning
